import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import type {
  InstanceLlmUsageEventsPage,
  InstanceLlmUsageSummary,
} from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn, formatCompactNumber, formatDateTime } from "@/lib/utils";

const PAGE_SIZE = 50;

// Events table: ten columns must fit a ~1450px viewport in every locale, so
// padding is tighter, headers drop the wide tracking and may wrap, and only
// timestamps and numbers are kept on one line.
const HEAD = "px-3 tracking-normal";
const NUM_HEAD = "px-3 tracking-normal text-right whitespace-nowrap";
const CELL = "px-3";
const NUM_CELL = "px-3 text-right tabular-nums whitespace-nowrap";
const WRAP = "whitespace-normal [overflow-wrap:anywhere]";
const ALL = "all";

interface Filters {
  dateFrom: string;
  dateTo: string;
  model: string;
  provider: string;
  purpose: string;
  status: string;
  employeeName: string;
}

const EMPTY_FILTERS: Filters = {
  dateFrom: "",
  dateTo: "",
  model: ALL,
  provider: ALL,
  purpose: "",
  status: ALL,
  employeeName: ALL,
};

function statusVariant(
  status: string
): "success" | "destructive" | "secondary" {
  if (status === "success") return "success";
  if (status === "error") return "destructive";
  return "secondary";
}

/** Turn the filter state into the querystring the API expects. */
function buildQuery(filters: Filters, page: number, limit: number): string {
  const qs = new URLSearchParams();
  if (filters.dateFrom) qs.set("dateFrom", `${filters.dateFrom}T00:00:00`);
  if (filters.dateTo) qs.set("dateTo", `${filters.dateTo}T23:59:59.999`);
  if (filters.model !== ALL) qs.set("model", filters.model);
  if (filters.provider !== ALL) qs.set("provider", filters.provider);
  if (filters.purpose.trim()) qs.set("purpose", filters.purpose.trim());
  if (filters.status !== ALL) qs.set("status", filters.status);
  if (filters.employeeName !== ALL) qs.set("employeeName", filters.employeeName);
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  return qs.toString();
}

export function InstanceUsagePage() {
  const { t } = useTranslation("instances");
  const { name } = useParams<{ name: string }>();
  const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = React.useState(1);

  function setFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
  }

  // Summary drives the model / employee filter option lists (and reflects the
  // chosen date range so the options track the window).
  const summaryQs = new URLSearchParams();
  if (filters.dateFrom) summaryQs.set("dateFrom", `${filters.dateFrom}T00:00:00`);
  if (filters.dateTo) summaryQs.set("dateTo", `${filters.dateTo}T23:59:59.999`);
  const { data: summary } = useQuery({
    queryKey: ["instance", name, "llm-usage", "options", summaryQs.toString()],
    queryFn: () =>
      api.get<InstanceLlmUsageSummary>(
        `/api/admin/instances/${name}/llm-usage?${summaryQs.toString()}`
      ),
    enabled: !!name,
  });

  const qs = buildQuery(filters, page, PAGE_SIZE);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["instance", name, "llm-usage-events", qs],
    queryFn: () =>
      api.get<InstanceLlmUsageEventsPage>(
        `/api/admin/instances/${name}/llm-usage/events?${qs}`
      ),
    enabled: !!name,
  });

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const modelOptions = summary?.byModel ?? [];
  const employeeOptions = (summary?.byEmployee ?? []).filter(
    (e) => e.employeeName && e.employeeName !== "Unknown"
  );

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
          <Link to={`/instances/${name}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> {t("usage.backToInstance")}
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("usage.title", { name })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t("usage.description")}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("usage.filters.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label htmlFor="f-from">{t("usage.filters.from")}</Label>
              <Input
                id="f-from"
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilter("dateFrom", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-to">{t("usage.filters.to")}</Label>
              <Input
                id="f-to"
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilter("dateTo", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-provider">{t("usage.filters.provider")}</Label>
              <Select
                value={filters.provider}
                onValueChange={(v) => setFilter("provider", v)}
              >
                <SelectTrigger id="f-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("usage.filters.allProviders")}</SelectItem>
                  <SelectItem value="openai">openai</SelectItem>
                  <SelectItem value="anthropic">anthropic</SelectItem>
                  <SelectItem value="gemini">gemini</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-model">{t("usage.filters.model")}</Label>
              <Select
                value={filters.model}
                onValueChange={(v) => setFilter("model", v)}
              >
                <SelectTrigger id="f-model">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("usage.filters.allModels")}</SelectItem>
                  {modelOptions.map((m) => (
                    <SelectItem key={`${m.provider}:${m.model}`} value={m.model}>
                      {m.model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-status">{t("usage.filters.status")}</Label>
              <Select
                value={filters.status}
                onValueChange={(v) => setFilter("status", v)}
              >
                <SelectTrigger id="f-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("usage.filters.allStatuses")}</SelectItem>
                  <SelectItem value="success">success</SelectItem>
                  <SelectItem value="error">error</SelectItem>
                  <SelectItem value="canceled">canceled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-employee">{t("usage.filters.employee")}</Label>
              <Select
                value={filters.employeeName}
                onValueChange={(v) => setFilter("employeeName", v)}
              >
                <SelectTrigger id="f-employee">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t("usage.filters.allEmployees")}</SelectItem>
                  {employeeOptions.map((e) => (
                    <SelectItem key={e.employeeName} value={e.employeeName}>
                      {e.employeeName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="f-purpose">{t("usage.filters.purpose")}</Label>
              <Input
                id="f-purpose"
                value={filters.purpose}
                onChange={(e) => setFilter("purpose", e.target.value)}
                placeholder={t("usage.filters.purposePlaceholder")}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>{t("usage.events.title")}</CardTitle>
            <span className="text-sm text-muted-foreground">
              {data?.unreachable
                ? t("usage.events.unreachableShort")
                : t("usage.events.range", { start: rangeStart, end: rangeEnd, total })}
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : data?.unreachable ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("usage.events.unreachable")}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t("usage.events.empty")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={HEAD}>{t("usage.columns.time")}</TableHead>
                  <TableHead className={HEAD}>{t("usage.columns.provider")}</TableHead>
                  <TableHead className={HEAD}>{t("usage.columns.model")}</TableHead>
                  <TableHead className={HEAD}>{t("usage.columns.purpose")}</TableHead>
                  <TableHead className={HEAD}>{t("usage.columns.employee")}</TableHead>
                  <TableHead className={HEAD}>{t("usage.columns.status")}</TableHead>
                  <TableHead className={NUM_HEAD}>{t("usage.columns.input")}</TableHead>
                  <TableHead className={NUM_HEAD}>{t("usage.columns.output")}</TableHead>
                  <TableHead className={NUM_HEAD}>{t("usage.columns.total")}</TableHead>
                  <TableHead className={NUM_HEAD}>{t("usage.columns.duration")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className={cn(CELL, "whitespace-nowrap")}>
                      {formatDateTime(r.createdAt)}
                    </TableCell>
                    <TableCell className={CELL}>{r.provider}</TableCell>
                    <TableCell className={CELL}>
                      <span className="block max-w-[12rem] truncate" title={r.model}>
                        {r.model}
                      </span>
                    </TableCell>
                    <TableCell className={cn(CELL, WRAP)}>{r.purpose}</TableCell>
                    <TableCell className={cn(CELL, WRAP, "min-w-[7rem] max-w-[12rem]")}>
                      {r.employeeName ?? "—"}
                    </TableCell>
                    <TableCell className={CELL}>
                      <Badge variant={statusVariant(r.status)}>{r.status}</Badge>
                    </TableCell>
                    <TableCell className={NUM_CELL}>
                      {formatCompactNumber(r.inputTokens)}
                    </TableCell>
                    <TableCell className={NUM_CELL}>
                      {formatCompactNumber(r.outputTokens)}
                    </TableCell>
                    <TableCell className={NUM_CELL}>
                      {formatCompactNumber(r.totalTokens)}
                    </TableCell>
                    <TableCell className={NUM_CELL}>
                      {r.durationMs === null ? "—" : t("usage.durationMs", { value: r.durationMs })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {total > PAGE_SIZE ? (
            <div className="flex items-center justify-end gap-2">
              <span className="text-sm text-muted-foreground">
                {t("usage.page", { page, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isFetching}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t("usage.previous")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isFetching}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t("usage.next")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
